import React from 'react';
import { useTranslation } from 'react-i18next';
import FormHome from '../../components/Formulaire/FormHome';
import ClientsCarousel from '../../components/Carousel/ClientsCarousel';
import InfoSn from '../../components/Info/InfoSn';
import InfoCI from '../../components/Info/InfoCI';
import InfoNe from '../../components/Info/InfoNe';
import { motion } from 'framer-motion';
import { LuArrowRight, LuCalculator } from 'react-icons/lu';

function Simulateur({ country, setCountry }) {
  const { t } = useTranslation();

  // Liste des pays et leur disponibilité
  const COUNTRIES = [
    { code: 'sn', name: t('countries.sn_legislation'), flag: '🇸🇳', available: true },
    { code: 'ci', name: t('countries.ci_legislation'), flag: '🇨🇮', available: false },
    { code: 'ne', name: t('countries.ne_legislation'), flag: '🇳🇪', available: false },
    { code: 'cm', name: t('countries.cm_legislation'), flag: '🇨🇲', available: false },
  ];
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  // Sélection du composant Info selon le pays
  const renderInfoComponent = () => {
    if (country === 'sn') return <InfoSn />;
    if (country === 'ci') return <InfoCI />;
    if (country === 'ne') return <InfoNe />;
    return (
      <div className="min-h-[400px] flex flex-col items-center justify-center">
        <div className="text-6xl mb-4">{COUNTRIES.find(c => c.code === country)?.flag}</div>
        <h3 className="text-2xl font-bold text-orange-600 mb-2">
          {t('simulator.developing_heading', { country: COUNTRIES.find(c => c.code === country)?.name })}
        </h3>
        <p className="text-gray-600 text-lg mb-6">{t('simulator.developing_body')}</p>
        <div className="max-w-xl bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center text-yellow-700">
          <p>
            {t('simulator.developing_note1', { country: COUNTRIES.find(c => c.code === country)?.name })}<br />
            {t('simulator.developing_note2')}
          </p>
        </div>
      </div>
    );
  };
  // NOTE: Le sélecteur de pays a été déplacé dans le Header pour une meilleure UX.
  // Ici on affiche simplement le pays sélectionné dans l'entête de la page.

  return (
    <div className="overflow-hidden">
      {/* Header Section */}
      <header className="relative bg-gradient-to-br from-orange-50 via-white to-orange-50 pt-16 pb-12">
        {/* Decorative Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-orange-200/20 to-yellow-200/20 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-blue-200/20 to-purple-200/20 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
            className="text-center"
          >
            {/* Badge */}
            <motion.div
              variants={itemVariants}
              className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-orange-200/50 rounded-full px-4 py-2 mb-6 shadow-lg"
            >
              <LuCalculator className="w-4 h-4 text-orange-500" />
              <span className="text-sm font-medium text-gray-700">{t('simulator.badge')}</span>
            </motion.div>

            {/* Main Title */}
            <motion.h1
              variants={itemVariants}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6"
            >
              {t('simulator.hero_title_line1')}{" "}
              <span className="bg-gradient-to-r from-orange-500 to-yellow-500 bg-clip-text text-transparent">
                {t('simulator.hero_title_highlight')}
              </span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              variants={itemVariants}
              className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-4"
            >
              {t('simulator.hero_subtitle')}
            </motion.p>
            
            {/* Affichage du pays sélectionné (contrôlé depuis le header) */}
            <motion.div variants={itemVariants} className="mt-4 inline-flex items-center gap-3 justify-center">
              <div className="inline-flex items-center gap-3 bg-white/80 backdrop-blur-sm border border-gray-100 rounded-full px-4 py-2">
                {/* <span className="text-2xl">{COUNTRIES.find(c => c.code === country)?.flag}</span> */}
                <span className="font-medium text-gray-800">{COUNTRIES.find(c => c.code === country)?.name}</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </header>

      {/* Form Section */}
      <section className="py-12">
        <motion.div 
          className="max-w-7xl mx-auto"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="">
            <FormHome country={country} setCountry={setCountry} />
          </div>
        </motion.div>
      </section>

      {/* Clients Section */}
      <motion.section 
        className="py-16 bg-gradient-to-b from-white to-gray-50"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <ClientsCarousel />
      </motion.section>

      {/* Info Section */}
      <motion.section
        className="py-16 bg-gradient-to-b from-gray-50 to-white"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('simulator.more_heading_line1')}{" "}
              <span className="bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent">
                {t('simulator.more_heading_highlight')}
              </span>
              ?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('simulator.more_subtitle')}
            </p>
          </div>

          <div className="flex flex-col items-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="group bg-gradient-to-r from-orange-500 to-yellow-500 text-white font-semibold px-8 py-4 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
              aria-label={t('simulator.try_yros_aria')}
              onClick={() => window.location.href = "/"}
            >
              {t('simulator.try_yros_cta')}
              <LuArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </motion.button>
          </div>
        </div>
        
        {renderInfoComponent()}
      </motion.section>
    </div>
  );
}

export default Simulateur;
